import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  ChevronRight,
  Dumbbell,
  GraduationCap,
  Headphones,
  Lock,
  RotateCcw,
  SlidersHorizontal,
  Sparkles,
  Volume2,
} from "lucide-react";
import { audioSupported, playNote, PRESETS } from "../lib/synthLab";
import {
  CONCEPT_ORDER,
  conceptRungIds,
  conceptLabel,
  RUNG_BY_ID,
} from "../lib/synthLadder";
import {
  emptyMastery,
  frontier,
  gildedCount,
  isConceptUnlocked,
  isRungUnlocked,
  rungStars,
  totalReps,
  totalRungCount,
} from "../lib/synthMastery";
import { loadMastery, clearMastery } from "../components/synthlab/masteryStore";
import LabShell from "../components/synthlab/LabShell";
import Stars from "../components/synthlab/Stars";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { cn } from "../lib/utils";

const CAN_PLAY = audioSupported();
const SOUND_CHECK_KEY = "synthLabSoundCheck";

const soundCheckDone = () => {
  try {
    return window.localStorage.getItem(SOUND_CHECK_KEY) === "1";
  } catch {
    return true;
  }
};

// One-time "can you actually hear this?" nudge. Playing a note from a tap is
// also what unlocks the AudioContext on iOS, so this doubles as the gesture
// that makes everything after it just work.
const SoundCheckCard = ({ onDone }) => (
  <Card className="mt-5 border-primary/40 bg-card/80 backdrop-blur">
    <CardHeader className="pb-3">
      <CardTitle className="flex items-center gap-2 text-base">
        <Headphones className="h-4 w-4 text-primary" />
        Quick sound check
      </CardTitle>
      <CardDescription>
        Ear training needs ears — headphones help a lot, especially on a phone
        (and check the silent switch on iPhones).
      </CardDescription>
    </CardHeader>
    <CardContent className="flex flex-wrap items-center gap-2">
      <Button size="sm" onClick={() => playNote(PRESETS[0].patch, 60, 800)}>
        <Volume2 className="h-4 w-4" />
        Play a test note
      </Button>
      <Button size="sm" variant="outline" onClick={onDone}>
        I hear it
      </Button>
    </CardContent>
  </Card>
);

// One concept's row on the journey map: its label, its rung stars, and a
// tap-through into targeted practice once it's unlocked.
const ConceptRow = ({ mastery, concept }) => {
  const unlocked = isConceptUnlocked(mastery, concept);
  const rungs = conceptRungIds(concept);
  const body = (
    <div
      className={cn(
        "flex flex-wrap items-center gap-x-4 gap-y-2 rounded-lg border px-3 py-2.5 transition-colors",
        unlocked
          ? "border-border bg-card/60 hover:border-primary/60 hover:bg-accent"
          : "border-border bg-card/40 opacity-60"
      )}
    >
      <div className="flex w-28 items-center gap-1.5 text-sm font-medium">
        {!unlocked && <Lock className="h-3.5 w-3.5 text-muted-foreground" />}
        {conceptLabel(concept)}
      </div>
      <div className="flex flex-1 flex-wrap items-center gap-x-3 gap-y-1">
        {rungs.map((rung) => {
          const open = isRungUnlocked(mastery, concept, rung);
          return (
            <span
              key={rung}
              className={cn("inline-flex items-center gap-1 text-xs", !open && "opacity-40")}
              title={`${RUNG_BY_ID[rung].label}${open ? "" : " (locked)"}`}
            >
              <span className="text-muted-foreground">{RUNG_BY_ID[rung].label}</span>
              {open ? (
                <Stars n={rungStars(mastery, concept, rung)} />
              ) : (
                <Lock className="h-3 w-3 text-muted-foreground/60" />
              )}
            </span>
          );
        })}
      </div>
      {unlocked && <ChevronRight className="h-4 w-4 text-muted-foreground" />}
    </div>
  );
  return unlocked ? (
    <Link to={`/synth-lab/practice?focus=${concept}`} className="block">
      {body}
    </Link>
  ) : (
    body
  );
};

const SynthLab = () => {
  const [mastery, setMastery] = useState(loadMastery);
  const [checked, setChecked] = useState(soundCheckDone);

  const dismissSoundCheck = () => {
    try {
      window.localStorage.setItem(SOUND_CHECK_KEY, "1");
    } catch {
      /* ignore */
    }
    setChecked(true);
  };

  const resetAll = () => {
    clearMastery();
    setMastery(emptyMastery());
  };

  const nextUp = frontier(mastery)[0] || null;
  const reps = totalReps(mastery);
  const gilded = gildedCount(mastery);

  return (
    <LabShell
      title="Synth Lab"
      blurb="A practice room for ear-training on a synth — learn each control, then earn its stars: hear it, name it, dial it in, fix a broken patch."
      actions={
        <>
          <Button asChild variant="outline" size="sm">
            <Link to="/synth-lab/learn">
              <GraduationCap className="h-4 w-4" />
              Learn
            </Link>
          </Button>
          <Button asChild variant="outline" size="sm">
            <Link to="/synth-lab/playground">
              <Sparkles className="h-4 w-4" />
              Playground
            </Link>
          </Button>
          <Button asChild variant="outline" size="sm">
            <Link to="/synth-lab/midi">
              <SlidersHorizontal className="h-4 w-4" />
              MIDI Setup
            </Link>
          </Button>
        </>
      }
    >
      {!CAN_PLAY && (
        <p className="mt-4 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          Your browser doesn't support Web Audio, so playback is unavailable —
          the pages still explain the concepts.
        </p>
      )}

      {CAN_PLAY && !checked && <SoundCheckCard onDone={dismissSoundCheck} />}

      {/* ---- the one button ---- */}
      <Card className="mt-5 bg-card/80 backdrop-blur">
        <CardContent className="flex flex-wrap items-center justify-between gap-3 py-5">
          <div>
            <p className="text-base font-semibold">
              {reps === 0 ? "Start your first session" : "Today's practice"}
            </p>
            <p className="text-sm text-muted-foreground">
              {reps === 0
                ? "A few minutes: meet the first concept, then train your ear on it."
                : nextUp
                ? `A short mixed session — next up: ${conceptLabel(nextUp.concept)} · ${RUNG_BY_ID[nextUp.rung].label}.`
                : "Everything's gilded — a maintenance session keeps the ear sharp."}
            </p>
          </div>
          <Button asChild size="lg">
            <Link to="/synth-lab/practice">
              <Dumbbell className="h-5 w-5" />
              Practice
            </Link>
          </Button>
        </CardContent>
      </Card>

      {/* ---- the journey map ---- */}
      <Card className="mt-5 bg-card/80 backdrop-blur">
        <CardHeader className="flex-row items-start justify-between gap-2 pb-3">
          <div>
            <CardTitle className="text-base">The journey</CardTitle>
            <CardDescription>
              {reps > 0
                ? `${reps} reps · ${gilded}/${totalRungCount()} rungs gilded. Tap a concept for targeted practice.`
                : "Each concept unlocks the next. Tap an unlocked concept to practice just that."}
            </CardDescription>
          </div>
          {reps > 0 && (
            <Button variant="ghost" size="sm" onClick={resetAll} title="Reset all progress">
              <RotateCcw className="h-4 w-4" />
              Reset
            </Button>
          )}
        </CardHeader>
        <CardContent className="grid gap-2">
          {CONCEPT_ORDER.map((concept) => (
            <ConceptRow key={concept} mastery={mastery} concept={concept} />
          ))}
        </CardContent>
      </Card>

      <p className="mt-4 text-center text-xs text-muted-foreground">
        Progress lives in this browser. Hardware knobs are optional — bind them
        in MIDI Setup and Dial/Fix rounds accept them as answers.
      </p>
    </LabShell>
  );
};

export default SynthLab;
